<script setup>
import { Column, DataTable, Menu, Section, SectionHeader } from '@dashhudson/dashing-ui';
import ExpandableSearch from '@/components/foundation/form/ExpandableSearch.vue';
import { useAuthStore } from '@/stores/auth';
import { useDashboardsStore } from '@/stores/dashboards';
import { useDashboardsShareableLinksStore } from '@/stores/dashboards-shareable-links';
import { computed, ref, watch } from 'vue';
import debounce from 'lodash/debounce';
import { debounceInputDelay } from '@/config';
import { getShareableLinkAvailability } from '@/app/dashboards/utils/sharing.utils';
import { useShareableLinkMenu } from '@/app/dashboards/composables/useShareableLinkMenu';
import DashingPageHeader from '@/components/dashingUI/DashingPageHeader.vue';

const authStore = useAuthStore();
const dashboardsStore = useDashboardsStore();
const dashboardsShareableLinksStore = useDashboardsShareableLinksStore();

function getLinkAvailability(linkData) {
  return getShareableLinkAvailability(linkData);
}

function getCreatedBy(linkData) {
  const createdByUser = dashboardsStore.dashboardSharableUsers
    .filter((user) => user.id === linkData.userId)
    .pop();
  if (createdByUser) {
    return `${createdByUser.firstName} ${createdByUser.lastName}`;
  }
  return `Unknown (ID: ${linkData.userId})`;
}

const { getMenuOptions } = useShareableLinkMenu();

const searchTerm = ref('');

function filterLinks(links) {
  if (!searchTerm.value) return links;
  const term = searchTerm.value.toLowerCase();
  return links.filter((link) => {
    const name = link.name?.toLowerCase() || '';
    const dashboardName = link.dashboardName?.toLowerCase() || '';
    const createdBy = getCreatedBy(link).toLowerCase();
    return name.includes(term) || dashboardName.includes(term) || createdBy.includes(term);
  });
}

const debounceInput = debounce((value) => {
  searchTerm.value = value;
}, debounceInputDelay);

const usersShareableLinks = computed(() =>
  filterLinks(dashboardsShareableLinksStore.usersShareableLinks),
);
const adminShareableLinks = computed(() =>
  filterLinks(dashboardsShareableLinksStore.adminShareableLinks),
);
const loadingUserLinks = computed(
  () => dashboardsShareableLinksStore.pending.getUserShareableLinks,
);
const loadingAdminLinks = computed(
  () => dashboardsShareableLinksStore.pending.getAdminShareableLinks,
);
const currentBrandName = computed(() => authStore.currentBrand?.name);

watch(
  () => authStore.identity,
  async () => {
    if (!authStore.identity) return;
    await Promise.all([
      dashboardsStore.getDashboardSharableUsers(),
      dashboardsShareableLinksStore.getUserShareableLinks({ userId: authStore.identity?.id }),
    ]);
  },
  { immediate: true },
);

watch(
  () => authStore.currentBrand,
  async () => {
    if (!authStore.currentBrand) return;
    await dashboardsShareableLinksStore.getAdminShareableLinks({
      brandId: authStore.currentBrand?.id,
    });
  },
  { immediate: true },
);
</script>

<template>
  <DashingPageHeader title="Shareable Links" hide-margin-class>
    <template #actions>
      <ExpandableSearch
        :model-value="searchTerm"
        :expanded-width="17"
        clearable
        large
        @update:model-value="debounceInput"
      />
    </template>
  </DashingPageHeader>
  <Section>
    <SectionHeader title="Links Created by You" hide-margin-class />
    <DataTable :value="usersShareableLinks" :loading="loadingUserLinks">
      <Column field="name" header="Name" class="dash-w-1/4 dash-max-w-[239px]">
        <template #body="{ data }">
          <span v-dash-truncate class="dash-typo-base-semibold">{{ data.name }}</span>
        </template>
      </Column>
      <Column header="Availability" class="dash-w-1/4">
        <template #body="{ data }">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div v-html="getLinkAvailability(data)" />
        </template>
      </Column>
      <Column field="dashboardName" header="Dashboard Name" class="dash-w-1/4" />
      <Column header="Created By" class="dash-w-1/4">
        <template #body="{ data }">
          <div>{{ getCreatedBy(data) }}</div>
        </template>
      </Column>
      <Column right-align>
        <template #body="{ data }">
          <Menu :model="getMenuOptions(data)" :dropdown="{ placement: 'bottom-end' }" />
        </template>
      </Column>
    </DataTable>
  </Section>
  <Section v-if="authStore.isCurrentBrandRoleAdmin">
    <SectionHeader :title="`All Other Links for ${currentBrandName}`" hide-margin-class />
    <DataTable :value="adminShareableLinks" :loading="loadingAdminLinks">
      <Column field="name" header="Name" class="dash-w-1/4 dash-max-w-[260px]">
        <template #body="{ data }">
          <span v-dash-truncate class="dash-typo-base-semibold">{{ data.name }}</span>
        </template>
      </Column>
      <Column header="Availability" class="dash-w-1/4">
        <template #body="{ data }">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div v-html="getLinkAvailability(data)" />
        </template>
      </Column>
      <Column field="dashboardName" header="Dashboard Name" class="dash-w-1/4" />
      <Column header="Created By" class="dash-w-1/4">
        <template #body="{ data }">
          <div>{{ getCreatedBy(data) }}</div>
        </template>
      </Column>
      <Column right-align>
        <template #body="{ data }">
          <Menu :model="getMenuOptions(data)" :dropdown="{ placement: 'bottom-end' }" />
        </template>
      </Column>
    </DataTable>
  </Section>
</template>
